import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Kanonische Domain der Website. Alle absoluten URLs leiten sich hiervon ab.
 * Muss mit der Domain in public/sitemap.xml, public/robots.txt und dem
 * Redirect in vercel.json uebereinstimmen.
 */
export const SITE_URL = 'https://www.apcleanservices.de';
export const SITE_NAME = 'AP Clean Services';

const DEFAULT_IMAGE = '/logo.png';

/** Macht aus einem Pfad eine absolute URL auf der kanonischen Domain. */
export const absoluteUrl = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
};

/**
 * Entfernt die statischen Fallback-SEO-Tags aus public/index.html.
 *
 * Hintergrund: Unter React 19 haengt react-helmet-async die Tags nicht mehr
 * selbst in den <head>, sondern rendert echte Elemente, die React dorthin
 * hoistet. Die bereits im HTML stehenden Tags werden dabei nicht ersetzt.
 * Ohne diesen Schritt haette jede Seite ein doppeltes <title>-, Canonical-
 * und Description-Tag - bei mehreren Canonicals ignoriert Google im Zweifel
 * alle.
 *
 * Crawler ohne JavaScript (Facebook, LinkedIn, WhatsApp) fuehren diesen
 * Effekt nie aus und sehen weiterhin die statischen Tags als Fallback.
 */
const useStaticSeoFallbackRemoved = (): void => {
  React.useEffect(() => {
    document.head.querySelectorAll('[data-static-seo]').forEach((el) => el.remove());
  }, []);
};

type SeoProps = {
  title: string;
  description: string;
  /** Pfad der Seite ohne Domain, z. B. '/praxisreinigung' oder '/' fuer die Startseite. */
  path: string;
  /** Vorschaubild fuer Social-Media-Previews (Pfad oder absolute URL). */
  image?: string;
};

/**
 * Zentrale SEO-Meta-Tags einer Seite: Title, Description, Canonical,
 * Open Graph und Twitter Cards.
 */
const Seo: React.FC<SeoProps> = ({ title, description, path, image = DEFAULT_IMAGE }) => {
  useStaticSeoFallbackRemoved();

  const canonical = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
};

export default Seo;
